import { useEffect, useRef } from "react";
import { readConfigFromScript } from "@/config";
import {
  getEnvFromScript,
  resolveEnvHosts,
  buildEnvironment,
  buildSseManager,
  sseLogoutListener,
  setLanguageRuntimeContext,
  startWidgetEffects,
  isDevMockLoggedInEnabled,
  buildDevMockLoggedInUser,
  isDevOpenChatbotPreviewEnabled,
  buildDevChatbotAuthPayload,
} from "@/core";

import { useWidgetStore } from "@/state";
import { fetchAlreadyLoggedIn } from "@/api";
import {
  clearLocalSession,
  notifyHostLogout,
  resolveWidgetReady,
  buildFallbackState,
  computePublicState,
} from "@/utils";
import type { UserInfo, WidgetPublicState } from "@/types";
import { setupAutoBind, teardownAutoBind } from "@/bootstrap";
import i18n from "@/i18n";

export const useBootstrap = () => {
  const setState = useWidgetStore((s) => s.setState);
  const setChatbotAuthPayload = useWidgetStore((s) => s.setChatbotAuthPayload);
  const setChatbotStatus = useWidgetStore((s) => s.setChatbotStatus);

  const bootstrappedRef = useRef<boolean>(false);

  useEffect(() => {
    let alive = true;
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    startWidgetEffects();

    (async () => {
      try {
        const cfg = readConfigFromScript("md-login");
        const env = getEnvFromScript() || "prod";
        const hosts = resolveEnvHosts(env);
        const environment = buildEnvironment(hosts);

        setLanguageRuntimeContext(cfg, environment);

        void i18n.changeLanguage(cfg.language ?? "el");

        const chatbot = cfg.chatbot;
        const cacheBreakerMin = cfg.cacheBreakerMin;

        const devMockLoggedIn = isDevMockLoggedInEnabled();

        const user: UserInfo | null = devMockLoggedIn
          ? buildDevMockLoggedInUser()
          : await fetchAlreadyLoggedIn(hosts.authHost, cfg.clientId, {
              chatbot: chatbot ? true : undefined,
              cacheBreakerMin,
            }).catch(() => null);

        if (devMockLoggedIn) {
          console.info(
            "[telekomId-widget] data-dev-mock-logged-in: skipping session fetch (dev only)",
          );
        }

        if (user?.installationId) {
          sessionStorage.setItem("cosIdInstallationId", user.installationId);
        }

        if (chatbot) {
          if (import.meta.env.DEV && isDevOpenChatbotPreviewEnabled()) {
            setChatbotAuthPayload(buildDevChatbotAuthPayload());
            setChatbotStatus("idle", null);
          } else if (user?.ticket) {
            setChatbotAuthPayload({
              FsVersion: "3",
              expires: 1800,
              ticket: user.ticket,
            });
            setChatbotStatus("idle", null);
          } else {
            setChatbotAuthPayload(null);
            setChatbotStatus("error", "Chatbot enabled but ticket is missing");
          }
        } else {
          setChatbotAuthPayload(null);
          setChatbotStatus("idle", null);
        }

        const publicState: WidgetPublicState = computePublicState(
          cfg,
          hosts,
          environment,
          user,
          env,
        );

        if (!alive) return;

        setState(publicState);
        setupAutoBind(cfg);

        if (
          import.meta.env.DEV &&
          isDevOpenChatbotPreviewEnabled() &&
          chatbot
        ) {
          console.info(
            "[telekomId-widget] data-dev-open-chatbot: opening chatbot modal (dev only)",
          );
          useWidgetStore.getState().setChatbotModalOpen(true);
        }

        if (!publicState.loggedIn) return;

        const installationId =
          publicState.user?.installationId ||
          sessionStorage.getItem("cosIdInstallationId") ||
          "";

        if (!installationId) return;

        const mgr = buildSseManager(
          hosts.cosIdHost,
          installationId,
          cfg.clientId,
        );

        sseLogoutListener.close();
        sseLogoutListener.start(mgr.bindLogoutUrl, () => {
          if (!alive) return;

          clearLocalSession();
          setState({ ready: true });

          const next: WidgetPublicState = {
            ...useWidgetStore.getState().state,
            ready: true,
            loggedIn: false,
            user: null,
          };

          setState(next);
          notifyHostLogout();
        });
      } catch (e) {
        console.error("[telekomId] bootstrap error:", e);
        if (!alive) return;

        const fallback = buildFallbackState();
        setState(fallback);
      } finally {
        resolveWidgetReady();
      }
    })();

    return () => {
      alive = false;
      sseLogoutListener.close();
      teardownAutoBind();
    };
  }, [setState]);
};
